/**
 * Takenbeheer Page (Epic C-1 — Fundament, Story C-1.3, UX-C2; kolom
 * "vereiste kwalificatie" ingevuld in Story C-2.3)
 *
 * Tabel + lege staat + filter-toggle + de aanmaak-/bewerk-/archiveer-flow.
 * Route `/planning/taken` (nav-integratie volgt in Story C-5.2).
 */
import { useMemo, useState } from 'react';
import { PLANNING_TASK_ACTIVITY_LABELS, activityUsesTargetMarket } from '@functieloonhuis/shared';
import { Layout } from '../../components/Layout';
import { useToast } from '../../components/Toast';
import { usePlanningTasks, useRestoreTask } from './hooks/usePlanningTasks';
import { useQualificationListItems } from './hooks/useQualificationLists';
import { TaakFormModal } from './components/TaakFormModal';
import { TaakArchiveerDialog } from './components/TaakArchiveerDialog';
import { ApiRequestError } from '../../lib/api';
import type { PlanningTaskDTO } from '@functieloonhuis/shared';

/**
 * Story C-2.3 (AC 3) — de triple leesbaar tonen, inclusief namen van
 * inmiddels gearchiveerde lijst-items (`includeArchived: true` — historie
 * blijft renderen, alleen niet meer als nieuwe keuze in het formulier).
 */
function useRequiredQualificationLabel(task: PlanningTaskDTO): string {
  const { data: productCategories } = useQualificationListItems('product-categories', true);
  const { data: targetMarkets } = useQualificationListItems('target-markets', true);

  return useMemo(() => {
    // Een lege doelmarkt betekende hier "geen eis". Dat klopt niet meer: bij
    // (her)controle is de doelmarkt altijd leeg, en die eisen zouden dus
    // allemaal onzichtbaar worden. Activiteit + categorie bepalen of er een
    // eis is; de doelmarkt is een detail dat er soms bij hoort.
    if (!task.requiredActivity || task.requiredProductCategoryId === null) {
      return '—';
    }
    const activityLabel = PLANNING_TASK_ACTIVITY_LABELS[task.requiredActivity];
    const categoryName =
      (productCategories ?? []).find((c) => c.id === task.requiredProductCategoryId)?.name ?? '—';
    if (!activityUsesTargetMarket(task.requiredActivity)) {
      return `${activityLabel} · ${categoryName}`;
    }
    const marketName =
      (targetMarkets ?? []).find((m) => m.id === task.requiredTargetMarketId)?.name ?? '—';
    return `${activityLabel} · ${categoryName} · ${marketName}`;
  }, [task.requiredActivity, task.requiredProductCategoryId, task.requiredTargetMarketId, productCategories, targetMarkets]);
}

function VereisteKwalificatieCell({ task }: { task: PlanningTaskDTO }) {
  const label = useRequiredQualificationLabel(task);
  return (
    <td data-testid={`taak-vereiste-kwalificatie-${task.id}`} className="py-2 pr-4 text-gray-600">
      {label}
    </td>
  );
}

export function TakenBeheerPage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data: tasks, isLoading } = usePlanningTasks(includeArchived);
  const restoreTask = useRestoreTask();
  const { showToast } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<PlanningTaskDTO | null>(null);
  const [archivingTask, setArchivingTask] = useState<PlanningTaskDTO | null>(null);

  function openCreateForm() {
    setEditingTask(null);
    setFormOpen(true);
  }

  function openEditForm(task: PlanningTaskDTO) {
    setEditingTask(task);
    setFormOpen(true);
  }

  async function handleRestore(task: PlanningTaskDTO) {
    try {
      await restoreTask.mutateAsync(task.id);
      showToast(`Taak '${task.name}' hersteld`, 'success');
    } catch (error) {
      const message =
        error instanceof ApiRequestError && error.code === 'TASK_NAME_TAKEN'
          ? 'Er bestaat al een actieve taak met deze naam — herstellen niet mogelijk.'
          : 'Herstellen is niet gelukt';
      showToast(message, 'error');
    }
  }

  const taskList = tasks ?? [];
  const showEmptyState = !isLoading && taskList.length === 0;

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-[#1E293B]">Taken</h1>
          <button
            type="button"
            data-testid="nieuwe-taak-button"
            onClick={openCreateForm}
            className="rounded-md bg-[#2F5A7A] px-4 py-2 text-sm font-medium text-white hover:bg-[#2F5A7A]/90"
          >
            + Nieuwe taak
          </button>
        </div>

        <label className="mb-4 flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            data-testid="toon-gearchiveerd-toggle"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="rounded border-[#E2E8F0] text-[#54949E] focus:ring-[#54949E]"
          />
          Toon gearchiveerd
        </label>

        {showEmptyState && (
          <div data-testid="taken-lege-staat" className="rounded-md border border-[#E2E8F0] py-12 text-center text-gray-500">
            <p>Nog geen taken — maak de eerste taak aan.</p>
            <button
              type="button"
              onClick={openCreateForm}
              className="mt-3 rounded-md bg-[#2F5A7A] px-4 py-2 text-sm font-medium text-white hover:bg-[#2F5A7A]/90"
            >
              + Nieuwe taak
            </button>
          </div>
        )}

        {!showEmptyState && (
          <table data-testid="taken-tabel" className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#E2E8F0] text-gray-500">
                <th className="py-2 pr-4 font-medium">Taak</th>
                <th className="py-2 pr-4 font-medium">Omschrijving</th>
                <th className="py-2 pr-4 font-medium">Vereiste kwalificatie</th>
                <th className="py-2 pr-4 font-medium"># indelingen (2 wkn)</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 font-medium">Acties</th>
              </tr>
            </thead>
            <tbody>
              {taskList.map((task) => (
                <tr key={task.id} data-testid={`taak-rij-${task.id}`} className="border-b border-[#E2E8F0]">
                  <td className="py-2 pr-4">
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="h-3 w-3 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: task.color ?? '#E2E8F0' }}
                      />
                      {task.name}
                    </span>
                  </td>
                  <td className="max-w-xs truncate py-2 pr-4 text-gray-600">{task.description ?? '—'}</td>
                  <VereisteKwalificatieCell task={task} />
                  <td className="py-2 pr-4 text-gray-600">{task.upcomingAssignmentCount}</td>
                  <td className="py-2 pr-4 text-gray-600">{task.isArchived ? 'Gearchiveerd' : 'Actief'}</td>
                  <td className="py-2">
                    {task.isArchived ? (
                      <button
                        type="button"
                        data-testid={`taak-herstellen-${task.id}`}
                        onClick={() => handleRestore(task)}
                        disabled={restoreTask.isPending}
                        className="text-sm font-medium text-[#54949E] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Herstellen
                      </button>
                    ) : (
                      <span className="flex gap-3">
                        <button
                          type="button"
                          data-testid={`taak-bewerken-${task.id}`}
                          onClick={() => openEditForm(task)}
                          className="text-sm font-medium text-[#54949E] hover:underline"
                        >
                          Bewerken
                        </button>
                        <button
                          type="button"
                          data-testid={`taak-archiveren-${task.id}`}
                          onClick={() => setArchivingTask(task)}
                          className="text-sm font-medium text-gray-600 hover:underline"
                        >
                          Archiveren
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <TaakFormModal isOpen={formOpen} onClose={() => setFormOpen(false)} task={editingTask} />
        <TaakArchiveerDialog isOpen={!!archivingTask} onClose={() => setArchivingTask(null)} task={archivingTask} />
      </div>
    </Layout>
  );
}
